/**
 * Ofis tarafı AI sayfası (katalog s. 27–28, tekoffice.co/firm/ai).
 *
 * Aynı v6 sayfası, ofis hesabında farklı düzen:
 *   · Solda dar sütun: orb ve durumu ("● Listening · Tap to stop").
 *   · Sağda konuşma: araç satırı, seçim düğmeleri, sayfa düğmesi, onay kartı
 *     (hepsi orb-agent'tan). Giriş: "Say or type what to do…".
 *   · Konuşma boşken "Where you left off": iş günlüğünden son işler ve
 *     "What were we doing?" (s. 28). Günlük kişiye özeldir (RLS).
 *   · Müşteri araçları (Document/Template sekmeleri, dosya +, gelir-gider
 *     kısayolları) ofiste yok: ofisin kendi defteri yok, belge müşteri
 *     çalışma alanında okunur.
 *
 * Müşteri hesabında hiçbir şeye dokunmaz; düzeni geri alır.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';

const g = globalThis as any;
const CUSTOMER_PLACEHOLDER = 'Ask about your income and expenses…';
const FIRM_PLACEHOLDER = 'Say or type what to do…';

interface Activity { kind: string; label: string; created_at: string; position: Record<string, unknown> | null }

let statusTimer: ReturnType<typeof setInterval> | null = null;

function el(id: string): HTMLElement | null { return document.getElementById(id); }
function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }
function isFirm(): boolean { const s = g.navSide?.(); return !!s && s !== 'customer'; }

const CSS = `
#v6.fwai #v6-tabbar,#v6.fwai .ai-brand,#v6.fwai #ai-bubble,#v6.fwai .ai-plus,
#v6.fwai #ai-mic-sel-btn,#v6.fwai #ai-voice-pick-btn,#v6.fwai #ai-quick-general{display:none!important}
#v6.fwai #v6-chat-panel-wrap{grid-template-columns:230px minmax(0,1fr);background:#000}
#v6.fwai #ai-hero-wrap{background:#000;border-right:1px solid var(--div);flex-direction:column}
#v6.fwai #ai-orb-canvas{width:min(78%,176px)}
#v6.fwai #ai-msgs{display:block!important;background:#111;padding:22px 26px 8px}
#v6.fwai .ai-compose{background:#111;padding:10px 26px 18px}
#v6.fwai .ai-thread .ai-msg .ai-av{display:none}
#v6.fwai .ai-thread .ai-msg .ai-bub{max-width:min(92%,640px);border-radius:12px;font-size:13px}
#v6.fwai .ai-thread .ai-msg.user .ai-bub{background:#2f63d8;border:none;color:#fff;border-radius:16px;padding:8px 14px}
#v6.fwai .ai-thread .ai-msg.bot .ai-bub{background:#161616;border:1px solid rgba(167,139,250,.32)}
#v6.fwai .ai-thread .ai-msg.bot .ai-bub:has(> .ag-confirm){background:none;border:none;padding:0}
#fwai-status{position:absolute;left:0;right:0;top:calc(50% + 104px);z-index:5;text-align:center;cursor:pointer}
#fwai-status .st{font-size:11px;font-weight:600;color:#8ab4ff}
#fwai-status .hint{font-size:10px;color:var(--t3);margin-top:2px}
#fwai-quick{display:flex;gap:6px;flex-wrap:wrap;margin-top:6px}
.ag-tools{font-size:10.5px;color:#a79fd6;margin-bottom:6px;font-family:ui-monospace,Menlo,Consolas,monospace}
.ag-choices{display:flex;gap:8px;flex-wrap:wrap;margin-top:10px}
.ag-page{display:inline-block;margin-top:10px;font-size:10.5px;padding:4px 10px;border-radius:6px;background:#262626;color:var(--t2);border:none;cursor:pointer}
.ag-page:hover{color:var(--txt)}
.ag-confirm{border:1px solid #d99a1e;border-radius:12px;padding:14px 16px;background:#141414;font-size:12.5px;line-height:1.7;margin:2px 0}
.ag-confirm-h{font-weight:700;margin-bottom:6px}
.ag-line{color:var(--txt)}
.ag-actions{display:flex;gap:8px;align-items:center;margin-top:12px}
.fwai-resume{border:1px solid var(--div);border-radius:12px;background:#0d0d0d;padding:14px 16px;margin-bottom:14px}
.fwai-resume h4{font-size:13px;margin:0 0 10px;display:flex;justify-content:space-between;align-items:center}
.fwai-item{display:flex;gap:10px;align-items:center;font-size:12px;padding:6px 0}
.fwai-dot{width:8px;height:8px;border-radius:50%;background:#43a047;flex-shrink:0}
.fwai-item.first{border:1px solid #b36b00;border-radius:10px;padding:10px 12px;margin-bottom:6px}
.fwai-item.first .fwai-dot{background:#ff9800;width:12px;height:12px}
.fwai-item .when{margin-left:auto;font-size:10.5px;color:var(--t2);white-space:nowrap}
@media (max-width:860px){
  #v6.fwai #v6-chat-panel-wrap{grid-template-columns:minmax(0,1fr);grid-template-rows:170px minmax(0,1fr) auto}
  #v6.fwai #ai-hero-wrap{border-right:none;border-bottom:1px solid var(--div)}
  #v6.fwai #ai-orb-canvas{height:120px;width:auto}
  #fwai-status{top:auto;bottom:8px}
  #v6.fwai #ai-msgs{padding:14px 16px 6px}
  #v6.fwai .ai-compose{padding:8px 16px 14px}
}`;

function installCss(): void {
  if (el('fwai-css')) return;
  const st = document.createElement('style');
  st.id = 'fwai-css';
  st.textContent = CSS;
  document.head.appendChild(st);
}

const VERB: Record<string, string> = {
  open_client: 'Opened', open_document: 'Opened', request_documents: 'Requested',
  link_client_code: 'Created a link code for', open_page: 'Opened',
};

function when(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return `Today ${time}`;
  if (d.toDateString() === y.toDateString()) return `Yesterday ${time}`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function describe(a: Activity): string {
  const client = typeof a.position?.client === 'string' ? ` for ${a.position.client}` : '';
  const page = a.kind === 'open_page' ? ` page` : '';
  return `${VERB[a.kind] ?? 'Worked on'} <b>${esc(a.label)}</b>${page}${esc(client)}`;
}

async function renderResume(): Promise<void> {
  const msgs = el('ai-msgs');
  const sb = legacy.sb;
  if (!msgs || !sb || msgs.querySelector('.ai-msg')) return;
  const r = await sb.from('activity_log').select('kind,label,created_at,position').order('created_at', { ascending: false }).limit(5);
  const rows: Activity[] = (r && r.data) || [];
  if (msgs.querySelector('.ai-msg')) return;
  const resumable = rows.findIndex((x) => ['open_client', 'open_document', 'request_documents'].includes(x.kind));
  const list = rows.map((a, i) => i === resumable
    ? `<div class="fwai-item first"><span class="fwai-dot"></span><div style="flex:1;min-width:0"><div>${describe(a)}</div>`
      + `<div style="margin-top:8px;text-align:right"><button class="btn br bsm" onclick="agentResume()">↩ Resume</button></div></div>`
      + `<span class="when" translate="no">${esc(when(a.created_at))}</span></div>`
    : `<div class="fwai-item"><span class="fwai-dot"${i > 2 ? ' style="background:#555"' : ''}></span><div>${describe(a)}</div><span class="when" translate="no">${esc(when(a.created_at))}</span></div>`).join('');
  msgs.innerHTML = `<div class="fwai-resume" id="fwai-resume"><h4>Where you left off${resumable >= 0 ? '<span style="font-size:10px;font-weight:700;color:#ff9800;background:rgba(255,152,0,.12);padding:2px 8px;border-radius:8px">1 to resume</span>' : ''}</h4>`
    + (list || '<div style="font-size:12px;color:var(--t2)">Nothing yet. Ask me to open a client, find what is missing, or request documents.</div>')
    + `</div>`;
}

function renderQuick(on: boolean): void {
  let q = el('fwai-quick');
  if (!on) { if (q) q.style.display = 'none'; return; }
  if (!q) {
    const host = el('ai-quick-general')?.parentElement;
    if (!host) return;
    q = document.createElement('div');
    q.id = 'fwai-quick';
    host.appendChild(q);
    const chips: Array<[string, string]> = [
      ['🎤 What were we doing?', 'What were we doing?'],
      ['⚠ Who is missing documents?', 'Which clients are missing documents?'],
      ['📥 What needs review today?', 'Which clients have documents to review?'],
    ];
    for (const [label, ask] of chips) {
      const b = document.createElement('button');
      b.className = 'fc';
      b.textContent = label;
      b.addEventListener('click', () => g.aiQuick?.(ask));
      q.appendChild(b);
    }
  }
  q.style.display = 'flex';
}

function renderStatus(on: boolean): void {
  let s = el('fwai-status');
  if (statusTimer) { clearInterval(statusTimer); statusTimer = null; }
  if (!on) { if (s) s.style.display = 'none'; return; }
  const hero = el('ai-hero-wrap');
  if (!hero) return;
  if (!s) {
    s = document.createElement('div');
    s.id = 'fwai-status';
    s.addEventListener('click', () => g.aiToggleMic?.());
    hero.appendChild(s);
  }
  s.style.display = '';
  const paint = () => {
    const box = el('fwai-status');
    if (!box) return;
    const conv = g._aiConvMode === true;
    const act = String(g._aiActivity ?? 'idle');
    const speaking = g._aiSpeakActive === true;
    const [st, hint] = !conv ? ['', 'Tap to talk']
      : speaking ? ['● Speaking', 'Tap to stop']
      : act === 'processing' ? ['● Thinking', 'Tap to stop']
      : ['● Listening', 'Tap to stop'];
    const html = (st ? `<div class="st">${st}</div>` : '') + `<div class="hint">${hint}</div>`;
    if (box.innerHTML !== html) box.innerHTML = html;
  };
  paint();
  statusTimer = setInterval(paint, 400);
}

function apply(): void {
  const v6 = el('v6');
  if (!v6) return;
  const firm = isFirm();
  installCss();
  v6.classList.toggle('fwai', firm);
  const inp = el('ai-inp') as HTMLInputElement | null;
  if (inp) inp.placeholder = firm ? FIRM_PLACEHOLDER : CUSTOMER_PLACEHOLDER;
  renderQuick(firm);
  renderStatus(firm);
  if (!firm) return;
  /* Ofiste yalnız asistan: belge/şablon sekmesi açık kaldıysa asistana dön. */
  if (typeof g.aipActiveTab === 'function' && g.aipActiveTab() !== 'asst') g.aipOpenAssistant?.();
  g.aiExpandChat?.();
  void renderResume();
}

export default defineFeature({
  id: 'firm-ai',
  label: 'Ofis AI sayfası',
  /* v6'nın sahibi doc-requests (kayıt tek modül izin verir); onun mount'u fwaiApply'ı çağırır. */
  view: null,
  legacyGlobals: {
    fwaiApply: () => apply(),
  },
});
